import React, { useState, useEffect, useRef } from 'react';
import api from '../api';

// ── Shared condition vocabulary (single source for StrategyBuilder + list ranking) ──
export const EMA_OPTIONS = ['ema08','ema09','ema12','ema20','ema21','ema26','ema50','ema70','ema100','ema150','ema200'];
export const EMA_LABELS  = { ema08:'EMA 8',ema09:'EMA 9',ema12:'EMA 12',ema20:'EMA 20',ema21:'EMA 21',ema26:'EMA 26',ema50:'EMA 50',ema70:'EMA 70',ema100:'EMA 100',ema150:'EMA 150',ema200:'EMA 200' };
export const IND_FIELDS  = [...EMA_OPTIONS,'rsi14','obv','bb_upper','bb_lower','atr14','stoch_k','stoch_d'];
export const BAR_FIELDS  = ['close','open','high','low','volume'];
export const ALL_FIELDS  = [...IND_FIELDS,...BAR_FIELDS,'candle'];
export const UNARY_OPS   = new Set(['green','red','rising','falling','higher_than_n_ago','lower_than_n_ago']);
export const OPS = [
  {value:'gt',label:'>'},{value:'gte',label:'>='},{value:'lt',label:'<'},{value:'lte',label:'<='},
  {value:'eq',label:'='},{value:'crosses_above',label:'crosses above'},{value:'crosses_below',label:'crosses below'},
  {value:'rising',label:'rising'},{value:'falling',label:'falling'},
  {value:'higher_than_n_ago',label:'higher than N days ago'},
  {value:'lower_than_n_ago',label:'lower than N days ago'},
  {value:'green',label:'green candle'},{value:'red',label:'red candle'},
];
export const LOOKBACK_OPS = new Set(['higher_than_n_ago','lower_than_n_ago']);
export const MARGIN_OPS = new Set(['gt','gte','lt','lte']);
export const AGG_OPS = new Set(['gt','gte','lt','lte','eq']);
export const MARGIN_UNITS = [{value:'pct',label:'%'},{value:'abs',label:'abs'},{value:'atr',label:'×ATR'}];

export function uid()  { return Math.random().toString(36).slice(2,9); }

export function blankCond()  { return {id:uid(),ticker:'',timeframe:'1d',lhs:'ema20',op:'gt',rhs:'ema50',streak:1,margin:0,marginUnit:'pct',lag:0,lhsOffset:0,rhsOffset:0,rhsAgg:'none',rhsAggN:13}; }
// True when the RHS aggregate (min/max over N) is active for this condition.
export function condAgg(c)  { return !UNARY_OPS.has(c.op)&&AGG_OPS.has(c.op)&&c.rhs!=='_num'&&c.rhsAgg&&c.rhsAgg!=='none'; }
export function serialiseCond(c)  { const agg=condAgg(c); return {ticker:c.ticker,timeframe:c.timeframe||'1d',lhs:c.lhs,op:c.op,rhs:UNARY_OPS.has(c.op)?'':(c.rhs==='_num'?String(c.rhsNum||0):c.rhs),streak:c.streak||1,margin:MARGIN_OPS.has(c.op)?(Number(c.margin)||0):0,margin_unit:c.marginUnit||'pct',lag:Number(c.lag)||0,lhs_offset:Number(c.lhsOffset)||0,rhs_offset:(UNARY_OPS.has(c.op)||agg)?0:(Number(c.rhsOffset)||0),rhs_agg:agg?c.rhsAgg:'none',rhs_agg_n:agg?(Number(c.rhsAggN)||0):0}; }
export function deserialiseCond(c){ const known=new Set(ALL_FIELDS); const isNum=c.rhs!==''&&!known.has(c.rhs)&&!UNARY_OPS.has(c.op); return {id:uid(),ticker:c.ticker||'',timeframe:c.timeframe||'1d',lhs:c.lhs,op:c.op,rhs:isNum?'_num':c.rhs||EMA_OPTIONS[0],rhsNum:isNum?c.rhs:'',streak:c.streak||1,margin:Number(c.margin)||0,marginUnit:c.margin_unit||'pct',lag:Number(c.lag)||0,lhsOffset:Number(c.lhs_offset)||0,rhsOffset:Number(c.rhs_offset)||0,rhsAgg:c.rhs_agg||'none',rhsAggN:Number(c.rhs_agg_n)||13}; }

// ── Shared mini-styles ────────────────────────────────────────────────────────
export const sSel    = {padding:'4px 5px',fontSize:'11px',border:'1px solid var(--border)',borderRadius:'var(--radius)',background:'var(--bg)',color:'var(--text)',fontFamily:"'DM Mono',monospace",flexShrink:0};
export const sMeta   = {fontSize:'11px',color:'var(--text-muted)',flexShrink:0};
export const sRemBtn = {padding:'2px 7px',border:'none',background:'#dc2626',color:'white',borderRadius:'3px',cursor:'pointer',fontSize:'11px',flexShrink:0};
export const sDrop   = {position:'absolute',top:'100%',left:0,right:0,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius)',zIndex:200,boxShadow:'0 4px 12px rgba(0,0,0,0.15)',maxHeight:'200px',overflowY:'auto',marginTop:'2px'};
export const sDI     = {padding:'6px 10px',cursor:'pointer',fontSize:'12px',display:'flex',gap:'8px',alignItems:'center',borderBottom:'1px solid var(--border)'};
export const sDSym   = {fontFamily:"'DM Mono',monospace",fontWeight:600,color:'var(--text)',minWidth:'80px',flexShrink:0};
export const sDName  = {color:'var(--text-muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'};
export function sIn(w){return {padding:'5px 8px',fontSize:'12px',border:'1px solid var(--border)',borderRadius:'var(--radius)',background:'var(--bg)',color:'var(--text)',fontFamily:"'DM Mono',monospace",width:w,boxSizing:'border-box'};}

// ── TickerSearch ──────────────────────────────────────────────────────────────
export function TickerSearch({value,onChange,placeholder='Search…',width='130px',error=false}) {
  const [q,setQ]   = useState(value||'');
  const [res,setR] = useState([]);
  const [open,setO]= useState(false);
  const timer=useRef(null);
  const wrap=useRef(null);
  useEffect(()=>{if(value&&value!==q)setQ(value);},[value]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{const h=e=>{if(wrap.current&&!wrap.current.contains(e.target))setO(false)};document.addEventListener('mousedown',h);return()=>document.removeEventListener('mousedown',h);},[]);
  const handleInput=val=>{setQ(val);clearTimeout(timer.current);if(!val.trim()){setR([]);setO(false);return;}timer.current=setTimeout(async()=>{try{const r=await api.get('/chart/search',{params:{q:val}});setR(r.data||[]);setO(true);}catch{setR([]);}},200);};
  const pick=(sym,exch)=>{const k=`${sym}:${exch}`;setQ(k);setO(false);setR([]);onChange(k);};
  return (
    <div ref={wrap} style={{position:'relative',width,flexShrink:0}}>
      <input style={{...sIn(width),...(error?{background:'#fde8e8',borderColor:'#dc2626'}:{})}} value={q} onChange={e=>handleInput(e.target.value)} onFocus={()=>q&&res.length&&setO(true)} placeholder={placeholder}/>
      {open&&res.length>0&&(
        <div style={sDrop}>
          {res.slice(0,8).map(r=>(
            <div key={`${r.symbol}:${r.exchange}`} style={sDI} onMouseDown={()=>pick(r.symbol,r.exchange)}>
              <span style={sDSym}>{r.symbol}:{r.exchange}</span>
              <span style={sDName}>{r.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── ConditionRow ──────────────────────────────────────────────────────────────
// hideTicker: omit the per-condition ticker field (used by list ranking, where the
// subject is always "this list member").
export function ConditionRow({cond,onChange,onRemove,error=false,hideTicker=false,allowTimeframe=false}) {
  const isUnary=UNARY_OPS.has(cond.op);
  const barUnit=allowTimeframe&&cond.timeframe&&cond.timeframe!=='1d'?'bars':'days';
  const isLookback=LOOKBACK_OPS.has(cond.op);
  const hasMargin=MARGIN_OPS.has(cond.op);
  const aggOp=AGG_OPS.has(cond.op);
  const aggOn=aggOp&&cond.rhs!=='_num'&&cond.rhsAgg&&cond.rhsAgg!=='none';
  return (
    <div style={{display:'flex',gap:'4px',alignItems:'center',marginBottom:'6px',flexWrap:'wrap'}}>
      {!hideTicker&&<TickerSearch value={cond.ticker} onChange={v=>onChange({...cond,ticker:v})} placeholder="Ticker…" width="120px" error={error}/>}
      {allowTimeframe&&<select style={sSel} title="Condition candle timeframe" value={cond.timeframe||'1d'} onChange={e=>onChange({...cond,timeframe:e.target.value})}>
        <option value="1m">1 min</option><option value="5m">5 min</option><option value="10m">10 min</option>
        <option value="15m">15 min</option><option value="30m">30 min</option><option value="1h">1 hour</option>
        <option value="2h">2 hour</option><option value="3h">3 hour</option><option value="4h">4 hour</option>
        <option value="1d">Daily</option>
      </select>}
      <select style={sSel} value={cond.lhs} onChange={e=>onChange({...cond,lhs:e.target.value})}>
        {ALL_FIELDS.map(f=><option key={f} value={f}>{EMA_LABELS[f]||f}</option>)}
      </select>
      <input style={sIn('40px')} type="number" min="0" title={`LHS: N prior ${barUnit} (0 = current candle)`} value={cond.lhsOffset??0} onChange={e=>onChange({...cond,lhsOffset:parseInt(e.target.value)||0})}/>
      <span style={sMeta}>{barUnit} ago</span>
      <select style={sSel} value={cond.op} onChange={e=>onChange({...cond,op:e.target.value,rhs:UNARY_OPS.has(e.target.value)?'':cond.rhs})}>
        {OPS.map(o=><option key={o.value} value={o.value}>{barUnit==='bars'?o.label.replace('days ago','bars ago'):o.label}</option>)}
      </select>
      {!isUnary&&(
        <select style={sSel} value={cond.rhs} onChange={e=>onChange({...cond,rhs:e.target.value})}>
          {ALL_FIELDS.filter(f=>f!=='candle').map(f=><option key={f} value={f}>{EMA_LABELS[f]||f}</option>)}
          <option value="_num">— value —</option>
        </select>
      )}
      {!isUnary&&cond.rhs==='_num'&&(
        <input style={sIn('60px')} type="number" placeholder="val" value={cond.rhsNum||''} onChange={e=>onChange({...cond,rhsNum:e.target.value})}/>
      )}
      {!isUnary&&cond.rhs!=='_num'&&aggOp&&(
        <select style={sSel} title={`RHS: single candle or lowest/highest over prior N ${barUnit}`} value={cond.rhsAgg||'none'} onChange={e=>onChange({...cond,rhsAgg:e.target.value})}>
          <option value="none">single day</option>
          <option value="min">lowest of last</option>
          <option value="max">highest of last</option>
        </select>
      )}
      {!isUnary&&cond.rhs!=='_num'&&aggOn&&(
        <>
          <input style={sIn('44px')} type="number" min="1" title="Window length N (trading days before today; today excluded)" value={cond.rhsAggN??13} onChange={e=>onChange({...cond,rhsAggN:parseInt(e.target.value)||1})}/>
          <span style={sMeta}>{barUnit}</span>
        </>
      )}
      {!isUnary&&cond.rhs!=='_num'&&!aggOn&&(
        <>
          <input style={sIn('40px')} type="number" min="0" title={`RHS: N prior ${barUnit} (0 = current candle)`} value={cond.rhsOffset??0} onChange={e=>onChange({...cond,rhsOffset:parseInt(e.target.value)||0})}/>
          <span style={sMeta}>{barUnit} ago</span>
        </>
      )}
      {hasMargin&&(
        <>
          <span style={sMeta}>by</span>
          <input style={sIn('52px')} type="number" min="0" step="any" placeholder="0" value={cond.margin??''} onChange={e=>onChange({...cond,margin:e.target.value})}/>
          <select style={sSel} value={cond.marginUnit||'pct'} onChange={e=>onChange({...cond,marginUnit:e.target.value})}>
            {MARGIN_UNITS.map(u=><option key={u.value} value={u.value}>{u.label}</option>)}
          </select>
        </>
      )}
      <span style={sMeta}>{isLookback?'vs':'for'}</span>
      <input style={sIn('44px')} type="number" min="1" value={cond.streak} onChange={e=>onChange({...cond,streak:parseInt(e.target.value)||1})}/>
      <span style={sMeta}>{isLookback?`${barUnit} ago`:barUnit}</span>
      {!isLookback&&(
        <>
          <span style={sMeta}>ending</span>
          <input style={sIn('44px')} type="number" min="0" value={cond.lag??0} onChange={e=>onChange({...cond,lag:parseInt(e.target.value)||0})}/>
          <span style={sMeta}>{barUnit} ago</span>
        </>
      )}
      <button style={sRemBtn} onClick={onRemove}>✕</button>
    </div>
  );
}
